import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { frontierSystems, inclusionCriteria } from '../data/frontier';
import { CriterionState, FrontierStatus } from '../types/content';
import { SourceRef } from '../components/SourceRef';
const STATUS_STYLE: Record<FrontierStatus, string> = {
  Candidate: 'border-[#7FE0CC]/50 text-[#7FE0CC]',
  Claimed: 'border-[#F0B23E]/50 text-[#F0B23E]',
  Disputed: 'border-[#E86850]/60 text-[#F08A74]',
  Retired: 'border-[#3A4A47] text-[#7E918C]'
};
const CRITERION_LABEL: Record<CriterionState, string> = {
  met: 'met',
  partial: 'partial',
  none: 'no evidence'
};
export function Frontier() {
  useEffect(() => {
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex';
    document.head.appendChild(meta);
    const prev = document.title;
    document.title = 'Deck 7σ — Agent Census';
    window.scrollTo(0, 0);
    return () => {
      meta.remove();
      document.title = prev;
    };
  }, []);
  return <div className="-mb-20 w-full bg-[#05070A] font-mono text-[#CFE6E0]" data-id="element-637" data-mp="2auz15b8xozru">
      <div className="border-b border-[#16211F]" data-id="element-638" data-mp="25v2sgypakk5y">
        <div className="mx-auto flex h-10 max-w-[1280px] items-center justify-between px-5 md:px-8" data-id="element-639" data-mp="25wnqiyatel8k">
          <span className="text-[11px] text-[#7E918C]" data-id="element-640" data-mp="2jnisrrqt6436">Agent Census · restricted observation deck</span>
          <span className="text-[11px] tracking-[0.2em] text-[#7FE0CC]" data-id="element-641" data-mp="2eu7q82t149h2">DECK 7σ · UNLISTED</span>
        </div>
      </div>

      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      duration: 0.25,
      ease: 'easeOut'
    }} className="mx-auto max-w-[1280px] px-5 pb-24 pt-12 md:px-8" data-id="element-642" data-mp="2olav7fp2x8lm">
        <div className="relative border border-[#16211F] p-6 md:p-10" data-id="element-643" data-mp="32izewk7eu9p3">
          {['left-0 top-0 border-l border-t', 'right-0 top-0 border-r border-t', 'bottom-0 left-0 border-b border-l', 'bottom-0 right-0 border-b border-r'].map(c => <span key={c} className={`absolute h-4 w-4 border-[#7FE0CC] ${c}`} aria-hidden="true" data-id="element-644" data-mp="145z1ro48odkw" />)}
          <p className="text-[11px] tracking-[0.24em] text-[#7E918C]" data-id="element-645" data-mp="33b0gzlb3rxls">SEALED OBSERVATION DECK — FRONTIER WATCH</p>
          <h1 className="mt-4 font-serif text-4xl leading-tight text-[#E8F3EF] md:text-6xl" data-id="element-646" data-mp="38a3g6p4ej9lu">Frontier candidate registry</h1>
          <p className="mt-6 max-w-3xl font-sans text-[15.5px] leading-relaxed text-[#B5CCC6]" data-id="element-647" data-mp="3d73nt4gwgkf2">
            Candidate registry for frontier and ASI-class systems. No agreed definition of ASI exists — inclusion criteria are ours and are published. Nothing here is an endorsement or a claim of sentience.
          </p>
        </div>

        <section aria-labelledby="criteria-title" className="mt-12 grid gap-8 md:grid-cols-[14rem_minmax(0,1fr)]" data-id="element-648" data-mp="3vz65mj5a26qw">
          <h2 id="criteria-title" className="text-[12px] tracking-[0.2em] text-[#7FE0CC]" data-id="element-649" data-mp="4t4ts8dhn947">
            INCLUSION CRITERIA
          </h2>
          <ol className="grid gap-px overflow-hidden border border-[#16211F] bg-[#16211F] sm:grid-cols-2" data-id="element-650" data-mp="9t99vxahi1tw">
            {inclusionCriteria.map((c, i) => <li key={c} className="flex gap-4 bg-[#05070A] p-5" data-id="element-651" data-mp="1ybtxuz81cc8v" data-mpi={i}>
                <span className="text-2xl text-[#7FE0CC]" data-id="element-652" data-mp="1y8zd16l40pex">{i + 1}</span>
                <span className="font-sans text-[14px] leading-relaxed text-[#CFE6E0]" data-id="element-653" data-mp="1u85qagjelgnm">{c}</span>
              </li>)}
          </ol>
        </section>

        <section aria-labelledby="registry-title" className="mt-14" data-id="element-654" data-mp="1kc2qhhc0xu3u">
          <div className="flex flex-wrap items-baseline justify-between gap-3" data-id="element-655" data-mp="1kdvsgjbozq5i">
            <h2 id="registry-title" className="text-[12px] tracking-[0.2em] text-[#7FE0CC]" data-id="element-656" data-mp="2i1e2ryl050bm">
              REGISTRY · {frontierSystems.length} SYSTEMS UNDER OBSERVATION
            </h2>
            <p className="text-[11px] text-[#7E918C]" data-id="element-657" data-mp="287x68mthd749">Criteria columns 1–4: ● met · ◐ partial · ○ no evidence</p>
          </div>
          <div className="mt-4 overflow-x-auto border border-[#16211F]" data-id="element-658" data-mp="238kky6i9c83s">
            <table className="w-full min-w-[1080px] border-collapse text-[13px]" data-id="element-659" data-mp="30r3vmg57fc0w">
              <thead className="text-left text-[11px] tracking-wider text-[#7E918C]" data-id="element-660" data-mp="2vyau01518jt1">
                <tr className="border-b border-[#16211F]" data-id="element-661" data-mp="2gfzyuz0lt8yu">
                  {['System', 'Lab', 'Claimed tier', 'Criteria 1–4', 'Evidence summary', 'Status', 'Last verified'].map(h => <th key={h} scope="col" className="whitespace-nowrap px-4 py-3 font-normal" data-id="element-662" data-mp="26jooiekkqt71">
                      {h}
                    </th>)}
                </tr>
              </thead>
              <tbody data-id="element-663" data-mp="2z8lsrdryapgs">
                {frontierSystems.map(s => <tr key={s.system} className="border-b border-[#16211F] align-top last:border-b-0" data-id="element-664" data-mp="346ds2suxddv0">
                    <th scope="row" className="whitespace-nowrap px-4 py-4 text-left font-normal text-[#E8F3EF]" data-id="element-665" data-mp="2pi99dovvh2c8">
                      {s.system}
                      <SourceRef href={s.sourceUrl} label={`${s.system} — primary source`} data-id="element-666" data-mp="2uezpgc7ofxxn" />
                    </th>
                    <td className="whitespace-nowrap px-4 py-4 text-[#9DB3AD]" data-id="element-667" data-mp="3i4bz1d6x280h">{s.lab}</td>
                    <td className={`whitespace-nowrap px-4 py-4 ${s.tier === 'ASI-claimed' ? 'text-[#F0B23E]' : 'text-[#CFE6E0]'}`} data-id="element-668" data-mp="3mz8012u4s5pq">{s.tier}</td>
                    <td className="whitespace-nowrap px-4 py-4 tracking-[0.35em] text-[#7FE0CC]" aria-label={s.criteria.map((c, i) => `criterion ${i + 1} ${CRITERION_LABEL[c]}`).join(', ')} data-id="element-669" data-mp="16jl5qzmjkpvr">
                      {s.criteria.map(c => c === 'met' ? '●' : c === 'partial' ? '◐' : '○').join('')}
                    </td>
                    <td className="max-w-md px-4 py-4 font-sans text-[13.5px] leading-relaxed text-[#B5CCC6]" data-id="element-670" data-mp="sk3aq2inkgdy">{s.evidence}</td>
                    <td className="whitespace-nowrap px-4 py-4" data-id="element-671" data-mp="nm1vjsocl17r">
                      <span className={`border px-2 py-0.5 text-[11px] ${STATUS_STYLE[s.status]}`} data-id="element-672" data-mp="11l1f4lx06px2">{s.status}</span>
                    </td>
                    <td className="whitespace-nowrap px-4 py-4 text-[#9DB3AD]" data-id="element-673" data-mp="wmzzkaysd8qc">{s.lastVerified}</td>
                  </tr>)}
              </tbody>
            </table>
          </div>
        </section>
      </motion.div>
    </div>;
}